import React, { useState } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert,
  Chip,
  Fade,
  Avatar,
  TextField,
  Button,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  ChildCare as ChildCareIcon,
  Favorite as FavoriteIcon,
  Restaurant as RestaurantIcon,
  Psychology as PsychologyIcon,
  Medication as MedicationIcon,
  Search as SearchIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Help as HelpIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 0 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ChildrenUpToYear: React.FC = () => {
  
  const [tabValue, setTabValue] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [compatibilityResult, setCompatibilityResult] = useState<any>(null);
  const [isSearching, setIsSearching] = useState(false);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };


  // Данные о лекарствах и их совместимости с грудным вскармливанием
  const medicationsData = [
    {
      name: 'Парацетамол',
      compatibility: 'safe',
      level: 'Безопасен',
      description: 'Совместим с грудным вскармливанием. Выделяется в грудное молоко в минимальных количествах.',
      recommendations: 'Можно принимать в обычных дозах. Не влияет на ребенка.',
      source: 'e-lactation.ru'
    },
    {
      name: 'Ибупрофен',
      compatibility: 'safe',
      level: 'Безопасен',
      description: 'Совместим с грудным вскармливанием. Низкая концентрация в грудном молоке.',
      recommendations: 'Рекомендуется принимать после кормления для снижения концентрации в молоке.',
      source: 'e-lactation.ru'
    },
    {
      name: 'Амоксициллин',
      compatibility: 'safe',
      level: 'Безопасен',
      description: 'Антибиотик, совместимый с грудным вскармливанием.',
      recommendations: 'Может вызывать диарею у ребенка. Следите за состоянием малыша.',
      source: 'e-lactation.ru'
    },
    {
      name: 'Ампициллин',
      compatibility: 'safe',
      level: 'Безопасен',
      description: 'Антибиотик пенициллинового ряда, совместимый с ГВ.',
      recommendations: 'Можно применять по показаниям. Следите за реакцией ребенка.',
      source: 'e-lactation.ru'
    },
    {
      name: 'Цефтриаксон',
      compatibility: 'safe',
      level: 'Безопасен',
      description: 'Цефалоспориновый антибиотик, совместимый с грудным вскармливанием.',
      recommendations: 'Применяется при серьезных инфекциях. Контролируйте состояние ребенка.',
      source: 'e-lactation.ru'
    },
    {
      name: 'Азитромицин',
      compatibility: 'safe',
      level: 'Безопасен',
      description: 'Макролидный антибиотик, совместимый с ГВ.',
      recommendations: 'Можно применять короткими курсами.',
      source: 'e-lactation.ru'
    },
    {
      name: 'Цитрамон',
      compatibility: 'caution',
      level: 'Осторожно',
      description: 'Содержит аспирин, который может вызывать синдром Рея у детей.',
      recommendations: 'Не рекомендуется при грудном вскармливании. Используйте парацетамол.',
      source: 'e-lactation.ru'
    },
    {
      name: 'Диклофенак',
      compatibility: 'caution',
      level: 'Осторожно',
      description: 'НПВС, может вызывать побочные эффекты у ребенка.',
      recommendations: 'Краткосрочное применение возможно. Длительное не рекомендуется.',
      source: 'e-lactation.ru'
    },
    {
      name: 'Нимесулид',
      compatibility: 'caution',
      level: 'Осторожно',
      description: 'НПВС, ограниченные данные о безопасности при ГВ.',
      recommendations: 'Избегайте длительного применения. Используйте альтернативы.',
      source: 'e-lactation.ru'
    },
    {
      name: 'Аспирин',
      compatibility: 'unsafe',
      level: 'Небезопасен',
      description: 'Противопоказан при грудном вскармливании из-за риска синдрома Рея.',
      recommendations: 'Избегайте применения. Используйте парацетамол или ибупрофен.',
      source: 'e-lactation.ru'
    },
    {
      name: 'Тетрациклин',
      compatibility: 'unsafe',
      level: 'Небезопасен',
      description: 'Может вызывать окрашивание зубов и нарушение роста костей у ребенка.',
      recommendations: 'Противопоказан при грудном вскармливании.',
      source: 'e-lactation.ru'
    },
    {
      name: 'Левомицетин',
      compatibility: 'unsafe',
      level: 'Небезопасен',
      description: 'Может вызывать угнетение костного мозга у ребенка.',
      recommendations: 'Противопоказан при грудном вскармливании.',
      source: 'e-lactation.ru'
    },
    {
      name: 'Валидол',
      compatibility: 'safe',
      level: 'Безопасен',
      description: 'Совместим с грудным вскармливанием в терапевтических дозах.',
      recommendations: 'Можно применять по показаниям.',
      source: 'e-lactation.ru'
    },
    {
      name: 'Но-шпа',
      compatibility: 'safe',
      level: 'Безопасен',
      description: 'Спазмолитик, совместимый с грудным вскармливанием.',
      recommendations: 'Можно применять при спазмах гладкой мускулатуры.',
      source: 'e-lactation.ru'
    },
    {
      name: 'Папаверин',
      compatibility: 'safe',
      level: 'Безопасен',
      description: 'Спазмолитик, совместимый с ГВ в терапевтических дозах.',
      recommendations: 'Применяется при спазмах внутренних органов.',
      source: 'e-lactation.ru'
    },
    {
      name: 'Супрастин',
      compatibility: 'caution',
      level: 'Осторожно',
      description: 'Антигистаминный препарат, может вызывать сонливость у ребенка.',
      recommendations: 'Краткосрочное применение возможно. Следите за состоянием ребенка.',
      source: 'e-lactation.ru'
    },
    {
      name: 'Лоратадин',
      compatibility: 'safe',
      level: 'Безопасен',
      description: 'Антигистаминный препарат второго поколения, совместимый с ГВ.',
      recommendations: 'Можно применять при аллергических реакциях.',
      source: 'e-lactation.ru'
    },
    {
      name: 'Цетиризин',
      compatibility: 'safe',
      level: 'Безопасен',
      description: 'Антигистаминный препарат, совместимый с грудным вскармливанием.',
      recommendations: 'Применяется при аллергических заболеваниях.',
      source: 'e-lactation.ru'
    },
    {
      name: 'Омепразол',
      compatibility: 'safe',
      level: 'Безопасен',
      description: 'Ингибитор протонной помпы, совместимый с ГВ.',
      recommendations: 'Применяется при заболеваниях ЖКТ.',
      source: 'e-lactation.ru'
    },
    {
      name: 'Ранитидин',
      compatibility: 'caution',
      level: 'Осторожно',
      description: 'Блокатор H2-рецепторов, ограниченные данные о безопасности.',
      recommendations: 'Применяйте с осторожностью, контролируйте состояние ребенка.',
      source: 'e-lactation.ru'
    },
    {
      name: 'Дротаверин',
      compatibility: 'safe',
      level: 'Безопасен',
      description: 'Спазмолитик, совместимый с грудным вскармливанием.',
      recommendations: 'Можно применять при спазмах гладкой мускулатуры.',
      source: 'e-lactation.ru'
    },
    {
      name: 'Метоклопрамид',
      compatibility: 'caution',
      level: 'Осторожно',
      description: 'Противорвотное средство, может влиять на лактацию.',
      recommendations: 'Применяйте с осторожностью, может снижать выработку молока.',
      source: 'e-lactation.ru'
    },
    {
      name: 'Домперидон',
      compatibility: 'safe',
      level: 'Безопасен',
      description: 'Противорвотное средство, может увеличивать выработку молока.',
      recommendations: 'Можно применять при тошноте и для стимуляции лактации.',
      source: 'e-lactation.ru'
    },
    {
      name: 'Активированный уголь',
      compatibility: 'safe',
      level: 'Безопасен',
      description: 'Энтеросорбент, не всасывается в кровь.',
      recommendations: 'Можно применять при отравлениях и диарее.',
      source: 'e-lactation.ru'
    },
    {
      name: 'Смекта',
      compatibility: 'safe',
      level: 'Безопасен',
      description: 'Энтеросорбент, совместимый с грудным вскармливанием.',
      recommendations: 'Применяется при диарее и заболеваниях ЖКТ.',
      source: 'e-lactation.ru'
    },
    {
      name: 'Лоперамид',
      compatibility: 'caution',
      level: 'Осторожно',
      description: 'Противодиарейное средство, может влиять на ребенка.',
      recommendations: 'Применяйте с осторожностью, краткосрочно.',
      source: 'e-lactation.ru'
    },
    {
      name: 'Фуросемид',
      compatibility: 'caution',
      level: 'Осторожно',
      description: 'Диуретик, может снижать выработку молока.',
      recommendations: 'Применяйте с осторожностью, может уменьшать лактацию.',
      source: 'e-lactation.ru'
    },
    {
      name: 'Верошпирон',
      compatibility: 'caution',
      level: 'Осторожно',
      description: 'Диуретик, может влиять на электролитный баланс.',
      recommendations: 'Применяйте с осторожностью, контролируйте состояние ребенка.',
      source: 'e-lactation.ru'
    },
    {
      name: 'Эналаприл',
      compatibility: 'caution',
      level: 'Осторожно',
      description: 'Ингибитор АПФ, ограниченные данные о безопасности.',
      recommendations: 'Применяйте с осторожностью, альтернативы предпочтительнее.',
      source: 'e-lactation.ru'
    },
    {
      name: 'Метформин',
      compatibility: 'safe',
      level: 'Безопасен',
      description: 'Гипогликемическое средство, совместимое с ГВ.',
      recommendations: 'Можно применять при сахарном диабете.',
      source: 'e-lactation.ru'
    },
    {
      name: 'Инсулин',
      compatibility: 'safe',
      level: 'Безопасен',
      description: 'Гормон, не проникает в грудное молоко.',
      recommendations: 'Можно применять при сахарном диабете.',
      source: 'e-lactation.ru'
    },
    {
      name: 'Преднизолон',
      compatibility: 'caution',
      level: 'Осторожно',
      description: 'Кортикостероид, может влиять на ребенка при длительном применении.',
      recommendations: 'Краткосрочное применение возможно. Длительное не рекомендуется.',
      source: 'e-lactation.ru'
    },
    {
      name: 'Дексаметазон',
      compatibility: 'caution',
      level: 'Осторожно',
      description: 'Кортикостероид, может подавлять функцию надпочечников у ребенка.',
      recommendations: 'Применяйте с осторожностью, краткосрочно.',
      source: 'e-lactation.ru'
    },
    {
      name: 'Тироксин',
      compatibility: 'safe',
      level: 'Безопасен',
      description: 'Гормон щитовидной железы, совместимый с ГВ.',
      recommendations: 'Можно применять при гипотиреозе.',
      source: 'e-lactation.ru'
    },
    {
      name: 'Варфарин',
      compatibility: 'caution',
      level: 'Осторожно',
      description: 'Антикоагулянт, может влиять на свертываемость крови у ребенка.',
      recommendations: 'Применяйте с осторожностью, контролируйте показатели крови.',
      source: 'e-lactation.ru'
    },
    {
      name: 'Гепарин',
      compatibility: 'safe',
      level: 'Безопасен',
      description: 'Антикоагулянт, не проникает в грудное молоко.',
      recommendations: 'Можно применять по показаниям.',
      source: 'e-lactation.ru'
    },
    {
      name: 'Дигоксин',
      compatibility: 'caution',
      level: 'Осторожно',
      description: 'Сердечный гликозид, может накапливаться у ребенка.',
      recommendations: 'Применяйте с осторожностью, контролируйте концентрацию в крови.',
      source: 'e-lactation.ru'
    },
    {
      name: 'Нитроглицерин',
      compatibility: 'safe',
      level: 'Безопасен',
      description: 'Вазодилататор, совместимый с грудным вскармливанием.',
      recommendations: 'Можно применять при стенокардии.',
      source: 'e-lactation.ru'
    },
    {
      name: 'Анаприлин',
      compatibility: 'caution',
      level: 'Осторожно',
      description: 'Бета-блокатор, может влиять на ребенка.',
      recommendations: 'Применяйте с осторожностью, контролируйте состояние ребенка.',
      source: 'e-lactation.ru'
    },
    {
      name: 'Амлодипин',
      compatibility: 'caution',
      level: 'Осторожно',
      description: 'Блокатор кальциевых каналов, ограниченные данные о безопасности.',
      recommendations: 'Применяйте с осторожностью, альтернативы предпочтительнее.',
      source: 'e-lactation.ru'
    },
    {
      name: 'Магнезия',
      compatibility: 'safe',
      level: 'Безопасен',
      description: 'Сульфат магния, совместимый с ГВ.',
      recommendations: 'Можно применять при дефиците магния.',
      source: 'e-lactation.ru'
    },
    {
      name: 'Кальций',
      compatibility: 'safe',
      level: 'Безопасен',
      description: 'Препараты кальция совместимы с грудным вскармливанием.',
      recommendations: 'Можно применять при дефиците кальция.',
      source: 'e-lactation.ru'
    },
    {
      name: 'Железо',
      compatibility: 'safe',
      level: 'Безопасен',
      description: 'Препараты железа совместимы с ГВ.',
      recommendations: 'Можно применять при железодефицитной анемии.',
      source: 'e-lactation.ru'
    },
    {
      name: 'Фолиевая кислота',
      compatibility: 'safe',
      level: 'Безопасен',
      description: 'Витамин B9, совместимый с грудным вскармливанием.',
      recommendations: 'Можно применять по показаниям.',
      source: 'e-lactation.ru'
    },
    {
      name: 'Витамин D',
      compatibility: 'safe',
      level: 'Безопасен',
      description: 'Витамин D совместим с ГВ.',
      recommendations: 'Рекомендуется для профилактики рахита у ребенка.',
      source: 'e-lactation.ru'
    },
    {
      name: 'Омега-3',
      compatibility: 'safe',
      level: 'Безопасен',
      description: 'Полиненасыщенные жирные кислоты, полезны для ребенка.',
      recommendations: 'Можно применять для улучшения качества молока.',
      source: 'e-lactation.ru'
    }
  ];

  // Функция поиска лекарства
  const searchMedication = (query: string) => {
    setIsSearching(true);
    const result = medicationsData.find(med => 
      med.name.toLowerCase().includes(query.toLowerCase())
    );
    setCompatibilityResult(result || null);
    setIsSearching(false);
  };

  // Функция получения цвета статуса
  const getStatusColor = (compatibility: string) => {
    switch (compatibility) {
      case 'safe': return '#4CAF50';
      case 'caution': return '#FF9800';
      case 'unsafe': return '#F44336';
      default: return '#9E9E9E';
    }
  };

  // Функция получения иконки статуса
  const getStatusIcon = (compatibility: string) => {
    switch (compatibility) {
      case 'safe': return <CheckIcon />;
      case 'caution': return <WarningIcon />;
      case 'unsafe': return <CloseIcon />;
      default: return <HelpIcon />;
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <ChildCareIcon sx={{ color: '#FF6B6B', mr: 2, fontSize: 40 }} />
          <Box>
            <Typography variant="h1" sx={{ 
              mb: 1,
              background: 'linear-gradient(45deg, #FF6B6B, #FF8E8E)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 700
            }}>
              Дети до года
            </Typography>
            <Typography variant="h6" sx={{ color: '#666', fontWeight: 400 }}>
              Полное руководство для педиатров
            </Typography>
          </Box>
        </Box>
        <Typography variant="body1" sx={{ color: '#718096', maxWidth: 800, fontSize: '1.1rem', lineHeight: 1.6 }}>
          Комплексное руководство по анатомо-физиологическим особенностям, уходу, 
          вскармливанию и введению прикорма для детей первого года жизни.
        </Typography>
      </Box>

      <Card sx={{ 
        mb: 3, 
        borderRadius: 3, 
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
      }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ 
                px: 3, 
                '& .MuiTab-root': { 
                  fontWeight: 600, 
                  textTransform: 'none',
                  fontSize: '1rem',
                  minHeight: 64,
                  minWidth: 200
                }
              }}
            >
              <Tab 
                label="Новорожденные" 
                icon={<PsychologyIcon />} 
                iconPosition="start"
              />
              <Tab 
                label="Прикорм" 
                icon={<RestaurantIcon />} 
                iconPosition="start"
              />
              <Tab 
                label="Навыки" 
                icon={<ChildCareIcon />} 
                iconPosition="start"
              />
              <Tab 
                label="Грудное и искусственное вскармливание" 
                icon={<FavoriteIcon />} 
                iconPosition="start"
              />
              <Tooltip title="Лекарственная совместимость при грудном вскармливании" arrow>
                <Tab 
                  label="Лекарства и ГВ" 
                  icon={<MedicationIcon />} 
                  iconPosition="start"
                />
              </Tooltip>
            </Tabs>
          </Box>
        </CardContent>
      </Card>

      <TabPanel value={tabValue} index={0}>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <PsychologyIcon sx={{ color: '#4A90E2', mr: 1, fontSize: 28 }} />
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#4A90E2' }}>
              Новорожденные
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ color: '#718096', mb: 3 }}>
            Физиологические особенности и состояния новорожденных детей
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, mb: 4 }}>
          <Box sx={{ flex: 1 }}>
            <Fade in={true} timeout={300}>
              <Card sx={{ 
                height: '100%',
                borderRadius: 3, 
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                background: 'linear-gradient(135deg, #E8F4FD 0%, #F0F8FF 100%)',
                border: '1px solid #4A90E2',
                transition: 'all 0.3s ease',
                '&:hover': { 
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 40px rgba(74, 144, 226, 0.2)'
                }
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ backgroundColor: '#4A90E2', mr: 2 }}>
                      <PsychologyIcon />
                    </Avatar>
                    <Typography variant="h5" sx={{ fontWeight: 600, color: '#2D3748' }}>
                      Кожа
                    </Typography>
                  </Box>
                  <List dense>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Токсическая эритема - красные пятна с белыми папулами"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Шелушение кожи - особенно на ладонях и стопах"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Мили - белые точки на носу и щеках"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Акне новорожденных - гормональные высыпания"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                  </List>
                  <Box sx={{ mt: 2, p: 2, backgroundColor: 'rgba(74, 144, 226, 0.1)', borderRadius: 2 }}>
                    <Typography variant="body2" sx={{ color: '#4A90E2', fontWeight: 600 }}>
                      💡 Важно: Все кожные проявления у новорожденных являются физиологическими
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Fade>
          </Box>

          <Box sx={{ flex: 1 }}>
            <Fade in={true} timeout={600}>
              <Card sx={{ 
                height: '100%',
                borderRadius: 3, 
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                background: 'linear-gradient(135deg, #E8F5E8 0%, #F0F8F0 100%)',
                border: '1px solid #50C878',
                transition: 'all 0.3s ease',
                '&:hover': { 
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 40px rgba(80, 200, 120, 0.2)'
                }
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ backgroundColor: '#50C878', mr: 2 }}>
                      <TrendingUpIcon />
                    </Avatar>
                    <Typography variant="h5" sx={{ fontWeight: 600, color: '#2D3748' }}>
                      Физиологические состояния
                    </Typography>
                  </Box>
                  <List dense>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Физиологическая убыль массы тела 3-10%"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Физиологическая желтуха - повышение билирубина"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Мочекислый инфаркт - оранжевые кристаллы в моче"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                  </List>
                  <Box sx={{ mt: 2, p: 2, backgroundColor: 'rgba(74, 144, 226, 0.1)', borderRadius: 2 }}>
                    <Typography variant="body2" sx={{ color: '#4A90E2', fontWeight: 600 }}>
                      💡 Важно: Все состояния являются физиологическими и проходят самостоятельно
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Fade>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, mb: 4 }}>
          <Box sx={{ flex: 1 }}>
            <Fade in={true} timeout={900}>
              <Card sx={{ 
                height: '100%',
                borderRadius: 3, 
                boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                background: 'linear-gradient(135deg, #F8F9FA 0%, #FFFFFF 100%)',
                border: '1px solid #E2E8F0',
                transition: 'all 0.2s ease',
                '&:hover': { 
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
                }
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ backgroundColor: '#4A90E2', mr: 2 }}>
                      <PsychologyIcon />
                    </Avatar>
                    <Typography variant="h5" sx={{ fontWeight: 600, color: '#2D3748' }}>
                      Гормональные особенности
                    </Typography>
                  </Box>
                  <List dense>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Нагрубание молочных желез - гормональный криз"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Гидроцеле (водянка яичек) - скопление жидкости"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Десквамативный вульвовагинит - выделения у девочек"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                  </List>
                  <Box sx={{ mt: 2, p: 2, backgroundColor: 'rgba(74, 144, 226, 0.1)', borderRadius: 2 }}>
                    <Typography variant="body2" sx={{ color: '#4A90E2', fontWeight: 600 }}>
                      💡 Важно: Гормональные проявления связаны с материнскими гормонами
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Fade>
          </Box>
        </Box>

        <Alert 
          severity="info" 
          sx={{ 
            mb: 3, 
            borderRadius: 3,
            boxShadow: '0 4px 12px rgba(74, 144, 226, 0.15)',
            background: 'linear-gradient(135deg, #F8F9FA 0%, #FFFFFF 100%)'
          }}
          icon={<InfoIcon sx={{ color: '#4A90E2' }} />}
        >
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            <strong>Важно:</strong> Все перечисленные состояния являются физиологическими 
            и не требуют специального лечения. При сомнениях обратитесь к педиатру.
          </Typography>
        </Alert>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ mb: 2, color: '#2D3748', fontWeight: 600 }}>
            Прикорм
          </Typography>
          <Typography variant="body1" sx={{ color: '#718096', mb: 3 }}>
            Схемы введения продуктов прикорма детям первого года жизни 
            на основе рекомендаций Союза педиатров России и ВОЗ.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 4 }}>
          <Card sx={{ 
            borderRadius: 3, 
            boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
            background: 'linear-gradient(135deg, #F8F9FA 0%, #FFFFFF 100%)',
            border: '1px solid #E2E8F0'
          }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h5" sx={{ mb: 3, color: '#2D3748', fontWeight: 600, textAlign: 'center' }}>
                🥕 Схема введения прикорма по месяцам
              </Typography>
              
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
                <Box>
                  <Typography variant="h6" sx={{ mb: 2, color: '#4A90E2', fontWeight: 600 }}>
                    1️⃣ Первый продукт (5-6 месяцев)
                  </Typography>
                  <List dense>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Овощное пюре (кабачок, цветная капуста, брокколи)"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Начинать с 1/2 чайной ложки"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                  </List>
                </Box>

                <Box>
                  <Typography variant="h6" sx={{ mb: 2, color: '#4A90E2', fontWeight: 600 }}>
                    2️⃣ Второй продукт (6 месяцев)
                  </Typography>
                  <List dense>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Безмолочная каша (рисовая, гречневая, кукурузная)"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Разводить грудным молоком или смесью"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                  </List>
                </Box>

                <Box>
                  <Typography variant="h6" sx={{ mb: 2, color: '#4A90E2', fontWeight: 600 }}>
                    3️⃣ Третий продукт (6-7 месяцев)
                  </Typography>
                  <List dense>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Фруктовое пюре (яблоко, груша, банан)"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="После введения овощей и каш"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                  </List>
                </Box>

                <Box>
                  <Typography variant="h6" sx={{ mb: 2, color: '#4A90E2', fontWeight: 600 }}>
                    4️⃣ Четвертый продукт (7-8 месяцев)
                  </Typography>
                  <List dense>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Мясное пюре (кролик, индейка, говядина)"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Начинать с 5-10 г, довести до 50-60 г"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                  </List>
                </Box>

                <Box>
                  <Typography variant="h6" sx={{ mb: 2, color: '#4A90E2', fontWeight: 600 }}>
                    5️⃣ Пятый продукт (8-9 месяцев)
                  </Typography>
                  <List dense>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Желток яйца (куриное, перепелиное)"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Начинать с 1/4 желтка, довести до 1/2"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                  </List>
                </Box>

                <Box>
                  <Typography variant="h6" sx={{ mb: 2, color: '#4A90E2', fontWeight: 600 }}>
                    6️⃣ Шестой продукт (9-10 месяцев)
                  </Typography>
                  <List dense>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Творог (детский, не более 50 г в день)"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Кефир (детский, не более 200 мл в день)"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                  </List>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ mb: 2, color: '#2D3748', fontWeight: 600 }}>
            Навыки
          </Typography>
          <Typography variant="body1" sx={{ color: '#718096', mb: 3 }}>
            Развитие навыков и умений детей первого года жизни по месяцам
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 4 }}>
          <Card sx={{ 
            borderRadius: 3, 
            boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
            background: 'linear-gradient(135deg, #F8F9FA 0%, #FFFFFF 100%)',
            border: '1px solid #E2E8F0'
          }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h5" sx={{ mb: 3, color: '#2D3748', fontWeight: 600, textAlign: 'center' }}>
                👶 Развитие навыков по месяцам
              </Typography>
              
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
                <Box>
                  <Typography variant="h6" sx={{ mb: 2, color: '#4A90E2', fontWeight: 600 }}>
                    1 месяц
                  </Typography>
                  <List dense>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Фиксирует взгляд на лице взрослого"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Реагирует на звук поворотом головы"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Удерживает голову в положении на животе"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                  </List>
                </Box>

                <Box>
                  <Typography variant="h6" sx={{ mb: 2, color: '#4A90E2', fontWeight: 600 }}>
                    2 месяца
                  </Typography>
                  <List dense>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Улыбается в ответ на улыбку"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Издает отдельные звуки"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Следит за движущимся предметом"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                  </List>
                </Box>

                <Box>
                  <Typography variant="h6" sx={{ mb: 2, color: '#4A90E2', fontWeight: 600 }}>
                    3 месяца
                  </Typography>
                  <List dense>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Хорошо держит голову в вертикальном положении"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Поворачивается на бок"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Гулит, смеется"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                  </List>
                </Box>

                <Box>
                  <Typography variant="h6" sx={{ mb: 2, color: '#4A90E2', fontWeight: 600 }}>
                    4 месяца
                  </Typography>
                  <List dense>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Переворачивается со спины на живот"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Тянется к игрушкам"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Узнает близких людей"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                  </List>
                </Box>

                <Box>
                  <Typography variant="h6" sx={{ mb: 2, color: '#4A90E2', fontWeight: 600 }}>
                    5 месяцев
                  </Typography>
                  <List dense>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Сидит с поддержкой"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Берет игрушку из рук взрослого"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Произносит слоги (ба, ма, да)"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                  </List>
                </Box>

                <Box>
                  <Typography variant="h6" sx={{ mb: 2, color: '#4A90E2', fontWeight: 600 }}>
                    6 месяцев
                  </Typography>
                  <List dense>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Сидит без поддержки"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Перекладывает игрушку из руки в руку"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Ест с ложки"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                  </List>
                </Box>

                <Box>
                  <Typography variant="h6" sx={{ mb: 2, color: '#4A90E2', fontWeight: 600 }}>
                    7-8 месяцев
                  </Typography>
                  <List dense>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Ползает на четвереньках"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Встает у опоры"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Понимает слово 'нельзя'"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                  </List>
                </Box>

                <Box>
                  <Typography variant="h6" sx={{ mb: 2, color: '#4A90E2', fontWeight: 600 }}>
                    9-10 месяцев
                  </Typography>
                  <List dense>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Ходит вдоль опоры"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Показывает части тела"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Говорит первые слова (мама, папа)"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                  </List>
                </Box>

                <Box>
                  <Typography variant="h6" sx={{ mb: 2, color: '#4A90E2', fontWeight: 600 }}>
                    11-12 месяцев
                  </Typography>
                  <List dense>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Ходит самостоятельно"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Строит башню из 2-3 кубиков"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Выполняет простые просьбы"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                  </List>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ mb: 2, color: '#4A90E2' }}>
            Грудное и искусственное вскармливание
          </Typography>
          <Typography variant="body1" sx={{ color: '#718096', mb: 3 }}>
            Современные принципы грудного и искусственного вскармливания детей первого года жизни 
            на основе рекомендаций ВОЗ, Союза педиатров России и Национальной программы оптимизации вскармливания.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, mb: 4 }}>
          <Card sx={{ 
            borderRadius: 3, 
            boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
            background: 'linear-gradient(135deg, #F8F9FA 0%, #FFFFFF 100%)',
            border: '1px solid #E2E8F0'
          }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h5" sx={{ mb: 3, color: '#2D3748', fontWeight: 600, textAlign: 'center' }}>
                🤱 Грудное вскармливание - принципы ВОЗ
              </Typography>
              
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
                <Box>
                  <Typography variant="h6" sx={{ mb: 2, color: '#4A90E2', fontWeight: 600 }}>
                    Основные принципы
                  </Typography>
                  <List dense>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Исключительно грудное вскармливание до 6 месяцев"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Кормление по требованию (8-12 раз в сутки)"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Частое прикладывание к груди"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Правильное прикладывание к груди"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                  </List>
                </Box>

                <Box>
                  <Typography variant="h6" sx={{ mb: 2, color: '#4A90E2', fontWeight: 600 }}>
                    Питание кормящей мамы
                  </Typography>
                  <List dense>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="НЕ нужна специальная диета"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Сбалансированное питание"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Достаточное потребление жидкости"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Исключить алкоголь и курение"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                  </List>
                </Box>

                <Box>
                  <Typography variant="h6" sx={{ mb: 2, color: '#4A90E2', fontWeight: 600 }}>
                    Преимущества ГВ
                  </Typography>
                  <List dense>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Защита от инфекций"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Оптимальный состав питательных веществ"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Укрепление эмоциональной связи"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                  </List>
                </Box>

                <Box>
                  <Typography variant="h6" sx={{ mb: 2, color: '#4A90E2', fontWeight: 600 }}>
                    Признаки достаточности ГВ
                  </Typography>
                  <List dense>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="6-8 мокрых подгузников в сутки"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Прибавка веса 125-200 г в неделю"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Ребенок спокоен между кормлениями"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                  </List>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ 
            borderRadius: 3, 
            boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
            background: 'linear-gradient(135deg, #F8F9FA 0%, #FFFFFF 100%)',
            border: '1px solid #E2E8F0'
          }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h5" sx={{ mb: 3, color: '#2D3748', fontWeight: 600, textAlign: 'center' }}>
                🍼 Искусственное вскармливание
              </Typography>
              
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
                <Box>
                  <Typography variant="h6" sx={{ mb: 2, color: '#4A90E2', fontWeight: 600 }}>
                    Типы смесей
                  </Typography>
                  <List dense>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Адаптированные (0-6 месяцев) - НАН, Нутрилон, Симилак"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Частично адаптированные (6-12 месяцев)"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Специальные (гипоаллергенные, безлактозные)"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                  </List>
                </Box>

                <Box>
                  <Typography variant="h6" sx={{ mb: 2, color: '#4A90E2', fontWeight: 600 }}>
                    Приготовление смеси
                  </Typography>
                  <List dense>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Кипяченая вода 36-37°C"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Строго по инструкции (1 мерная ложка на 30 мл)"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Тщательно размешать до полного растворения"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Не хранить готовую смесь более 1 часа"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                  </List>
                </Box>

                <Box>
                  <Typography variant="h6" sx={{ mb: 2, color: '#4A90E2', fontWeight: 600 }}>
                    Режим кормления
                  </Typography>
                  <List dense>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Кормление по часам (каждые 3-3.5 часа)"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="6-7 кормлений в сутки"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Ночной перерыв 6-6.5 часов"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                  </List>
                </Box>

                <Box>
                  <Typography variant="h6" sx={{ mb: 2, color: '#4A90E2', fontWeight: 600 }}>
                    Объем кормления по возрасту
                  </Typography>
                  <List dense>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="1-2 месяца: 90-120 мл"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="3-4 месяца: 120-150 мл"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="5-6 месяцев: 150-180 мл"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#4A90E2', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="7-12 месяцев: 180-220 мл"
                        sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem', fontWeight: 500 } }}
                      />
                    </ListItem>
                  </List>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Важно:</strong> Грудное вскармливание является оптимальным для ребенка. Искусственное вскармливание 
            применяется только при невозможности грудного вскармливания. Выбор смеси и режима кормления должен 
            осуществляться под контролем педиатра с учетом индивидуальных особенностей ребенка.
          </Typography>
        </Alert>
      </TabPanel>

      <TabPanel value={tabValue} index={4}>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <MedicationIcon sx={{ color: '#4A90E2', mr: 1, fontSize: 28 }} />
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#4A90E2' }}>
              Лекарственная совместимость при грудном вскармливании
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ color: '#718096', mb: 3 }}>
            Проверка совместимости лекарственных препаратов с грудным вскармливанием на основе данных 
            <a href="https://e-lactation.ru" target="_blank" rel="noopener noreferrer" style={{ color: '#4A90E2', textDecoration: 'none' }}>
              e-lactation.ru
            </a> - база из 22,362 лекарств
          </Typography>
        </Box>

        {/* Поиск лекарства */}
        <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.1)' }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#4A90E2' }}>
              Поиск лекарственного препарата
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
              <TextField
                fullWidth
                label="Название лекарства"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Например: парацетамол, ибупрофен..."
                InputProps={{
                  startAdornment: <SearchIcon sx={{ color: '#4A90E2', mr: 1 }} />
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              <Button
                variant="contained"
                onClick={() => searchMedication(searchQuery)}
                disabled={!searchQuery.trim() || isSearching}
                startIcon={<SearchIcon />}
                sx={{
                  borderRadius: 2,
                  background: 'linear-gradient(45deg, #4A90E2, #50C878)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #357ABD, #40B868)',
                  }
                }}
              >
                {isSearching ? 'Поиск...' : 'Найти'}
              </Button>
            </Box>
            
            {/* Быстрый выбор популярных лекарств */}
            <Typography variant="body2" sx={{ mb: 1, color: '#666' }}>
              Популярные препараты:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {['Парацетамол', 'Ибупрофен', 'Амоксициллин', 'Цитрамон', 'Диклофенак', 'Аспирин'].map((med) => (
                <Chip
                  key={med}
                  label={med}
                  onClick={() => {
                    setSearchQuery(med);
                    searchMedication(med);
                  }}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: '#E3F2FD'
                    }
                  }}
                />
              ))}
            </Box>
          </CardContent>
        </Card>

        {/* Результат поиска */}
        {compatibilityResult && (
          <Fade in={!!compatibilityResult}>
            <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.1)' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    p: 1, 
                    borderRadius: 2, 
                    backgroundColor: getStatusColor(compatibilityResult.compatibility) + '20',
                    mr: 2
                  }}>
                    {getStatusIcon(compatibilityResult.compatibility)}
                  </Box>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 600, color: '#4A90E2' }}>
                      {compatibilityResult.name}
                    </Typography>
                    <Chip 
                      label={compatibilityResult.level}
                      sx={{ 
                        backgroundColor: getStatusColor(compatibilityResult.compatibility),
                        color: 'white',
                        fontWeight: 600
                      }}
                    />
                  </Box>
                </Box>

                <Typography variant="body1" sx={{ mb: 2, color: '#333' }}>
                  {compatibilityResult.description}
                </Typography>

                <Alert 
                  severity={compatibilityResult.compatibility === 'safe' ? 'success' : 
                           compatibilityResult.compatibility === 'caution' ? 'warning' : 'error'}
                  sx={{ mb: 2 }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    Рекомендации:
                  </Typography>
                  <Typography variant="body2">
                    {compatibilityResult.recommendations}
                  </Typography>
                </Alert>

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    Источник: {compatibilityResult.source}
                  </Typography>
                  <Button
                    size="small"
                    href="https://e-lactation.ru"
                    target="_blank"
                    rel="noopener noreferrer"
                    endIcon={<InfoIcon />}
                    sx={{ color: '#4A90E2' }}
                  >
                    Подробнее на e-lactation.ru
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Fade>
        )}

        {/* Справочная информация */}
        <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.1)' }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#4A90E2' }}>
              Справочная информация
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ mb: 1, color: '#4CAF50' }}>
                  ✅ Безопасные препараты
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                  Лекарства, которые можно принимать при грудном вскармливании без риска для ребенка.
                </Typography>
                <List dense>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: '#4CAF50', fontSize: 16 }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Парацетамол"
                      sx={{ '& .MuiListItemText-primary': { fontSize: '0.875rem' } }}
                    />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: '#4CAF50', fontSize: 16 }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Ибупрофен"
                      sx={{ '& .MuiListItemText-primary': { fontSize: '0.875rem' } }}
                    />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: '#4CAF50', fontSize: 16 }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Амоксициллин"
                      sx={{ '& .MuiListItemText-primary': { fontSize: '0.875rem' } }}
                    />
                  </ListItem>
                </List>
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ mb: 1, color: '#FF9800' }}>
                  ⚠️ Осторожно
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                  Препараты, требующие осторожности и консультации врача.
                </Typography>
                <List dense>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon>
                      <WarningIcon sx={{ color: '#FF9800', fontSize: 16 }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Диклофенак"
                      sx={{ '& .MuiListItemText-primary': { fontSize: '0.875rem' } }}
                    />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon>
                      <WarningIcon sx={{ color: '#FF9800', fontSize: 16 }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Цитрамон"
                      sx={{ '& .MuiListItemText-primary': { fontSize: '0.875rem' } }}
                    />
                  </ListItem>
                </List>
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ mb: 1, color: '#F44336' }}>
                  ❌ Противопоказаны
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                  Лекарства, которые нельзя принимать при грудном вскармливании.
                </Typography>
                <List dense>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon>
                      <CloseIcon sx={{ color: '#F44336', fontSize: 16 }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Аспирин"
                      sx={{ '& .MuiListItemText-primary': { fontSize: '0.875rem' } }}
                    />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon>
                      <CloseIcon sx={{ color: '#F44336', fontSize: 16 }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Тетрациклин"
                      sx={{ '& .MuiListItemText-primary': { fontSize: '0.875rem' } }}
                    />
                  </ListItem>
                </List>
              </Box>
            </Box>

            <Alert severity="info" sx={{ mt: 3 }}>
              <Typography variant="body2">
                <strong>Важно:</strong> Данная информация носит справочный характер. 
                Перед применением любого лекарственного препарата при грудном вскармливании 
                обязательно проконсультируйтесь с врачом. Для получения актуальной информации 
                о совместимости лекарств с грудным вскармливанием используйте 
                <a href="https://e-lactation.ru" target="_blank" rel="noopener noreferrer" style={{ color: '#4A90E2', textDecoration: 'none' }}>
                  e-lactation.ru
                </a>.
              </Typography>
            </Alert>
          </CardContent>
        </Card>
      </TabPanel>
    </Box>
  );
};

export default ChildrenUpToYear;
